import React, { useEffect, useState } from 'react';
import { Alert, Button, Drawer, Input, message, Popconfirm, Space, Tag, Typography } from 'antd';
import { ExperimentOutlined, SaveOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { lambda } from 'src/_shared/services/utils.js';

const { Text, Paragraph } = Typography;

const ENDPOINT = 'mapping-config-api';

/**
 * Mapping editor — view/edit the per-(company, taskType) integration config
 * doc (field overrides, customValues, statusRules, policy knobs). Backed by
 * the mapping-config-api Lambda, which validates with the same compiler the
 * workers run, so a doc that saves is a doc that runs. "Test with latest
 * file" creates a dry-run request carrying the UNSAVED candidate doc — the
 * report shows the would-be writes before anything is persisted.
 *
 * No doc is a normal, healthy state: the company runs on the default header
 * ladders (most feeds need no doc at all).
 */
const skeletonDoc = (companyId, taskType) =>
	JSON.stringify(
		{
			companyId,
			taskType,
			note: '',
			fields: {},
			policy: {},
		},
		null,
		2
	);

const MappingEditorDrawer = ({ row, onClose, onTestRun, currentUser }) => {
	const [loading, setLoading] = useState(false);
	const [docText, setDocText] = useState('');
	const [meta, setMeta] = useState(null); // { updatedAt, updatedBy, historyCount } | 'none'
	const [validation, setValidation] = useState(null); // { ok, error?, fields?, policy? }
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		if (!row) return;
		setDocText('');
		setValidation(null);
		setMeta(null);
		setLoading(true);
		lambda({
			endpoint: ENDPOINT,
			variables: { action: 'get', companyId: row.companyId, taskType: row.syncType },
		})
			.then((res) => {
				const raw = res?.data ?? res;
				const payload = typeof raw === 'string' ? JSON.parse(raw) : raw;
				if (payload?.ok === false) throw new Error(payload.error);
				const doc = payload?.doc;
				if (doc) {
					const { history, ...editable } = doc;
					setDocText(JSON.stringify(editable, null, 2));
					setMeta({
						updatedAt: doc.updatedAt,
						updatedBy: doc.updatedBy,
						historyCount: (history || []).length,
					});
				} else {
					setDocText(skeletonDoc(row.companyId, row.syncType));
					setMeta('none');
				}
			})
			.catch((error) => {
				message.error(`Could not load mapping config: ${error?.message || 'unknown error'}`);
				onClose();
			})
			.finally(() => setLoading(false));
	}, [row]);

	const parseDoc = () => {
		try {
			return JSON.parse(docText);
		} catch (error) {
			setValidation({ ok: false, error: `Not valid JSON: ${error.message}` });
			return null;
		}
	};

	const callApi = async (variables) => {
		const res = await lambda({ endpoint: ENDPOINT, variables });
		const raw = res?.data ?? res;
		const payload = typeof raw === 'string' ? JSON.parse(raw) : raw;
		if (payload?.ok === false) throw new Error(payload.error);
		return payload;
	};

	const validate = async () => {
		const doc = parseDoc();
		if (!doc) return null;
		setBusy(true);
		try {
			const result = await callApi({ action: 'validate', doc });
			setValidation({ ok: true, fields: result.fields, policy: result.policy });
			return doc;
		} catch (error) {
			setValidation({ ok: false, error: error?.message || 'validation failed' });
			return null;
		} finally {
			setBusy(false);
		}
	};

	const save = async () => {
		const doc = await validate();
		if (!doc) return;
		setBusy(true);
		try {
			await callApi({
				action: 'upsert',
				doc,
				updatedByEmail: currentUser?.emailAddress,
			});
			message.success(`Mapping config saved for ${row.companyName} ${row.syncType}.`);
			onClose();
		} catch (error) {
			message.error(`Save failed: ${error?.message || 'unknown error'}`);
		} finally {
			setBusy(false);
		}
	};

	const testRun = async () => {
		const doc = await validate();
		if (!doc) return;
		onTestRun(doc);
		onClose();
	};

	return (
		<Drawer
			title={row ? `Mapping config — ${row.companyName} ${row.syncType}` : ''}
			width={720}
			visible={Boolean(row)}
			onClose={onClose}
		>
			{row ? (
				<div>
					{meta === 'none' ? (
						<Alert
							type="info"
							showIcon
							style={{ marginBottom: 12 }}
							message="No config doc — this company runs on the default mappings."
							description="That is the normal state for most feeds. Only save a doc if the defaults need overriding (field mappings, statusRules, or policy knobs like skipToHeader / rowFilter)."
						/>
					) : meta ? (
						<Paragraph type="secondary" style={{ marginBottom: 12 }}>
							Last saved {meta.updatedAt || 'unknown'} by {meta.updatedBy || 'unknown'}
							{meta.historyCount ? ` — ${meta.historyCount} prior version${meta.historyCount > 1 ? 's' : ''} kept` : ''}
						</Paragraph>
					) : null}

					<Input.TextArea
						value={docText}
						rows={24}
						spellCheck={false}
						style={{ fontFamily: 'monospace', fontSize: 12 }}
						disabled={loading || busy}
						onChange={(e) => {
							setDocText(e.target.value);
							setValidation(null);
						}}
					/>

					{validation ? (
						validation.ok ? (
							<Alert
								type="success"
								showIcon
								style={{ marginTop: 12 }}
								message="Doc compiles clean"
								description={
									<span>
										Field overrides:{' '}
										{validation.fields?.length ? (
											validation.fields.map((f) => <Tag key={f}>{f}</Tag>)
										) : (
											<Text type="secondary">none (defaults)</Text>
										)}
										{' — policy: '}
										{validation.policy?.length ? (
											validation.policy.map((p) => <Tag key={p} color="purple">{p}</Tag>)
										) : (
											<Text type="secondary">none</Text>
										)}
									</span>
								}
							/>
						) : (
							<Alert type="error" showIcon style={{ marginTop: 12 }} message={validation.error} />
						)
					) : null}

					<Space style={{ marginTop: 16 }} wrap>
						<Button
							icon={<SafetyCertificateOutlined />}
							loading={busy}
							disabled={loading}
							onClick={validate}
						>
							Validate
						</Button>
						<Button
							icon={<ExperimentOutlined />}
							loading={busy}
							disabled={loading}
							onClick={testRun}
						>
							Test with latest file
						</Button>
						<Popconfirm
							title={`Save this config for ${row.companyName} ${row.syncType}? The NEXT run uses it for real writes.`}
							okText="Save"
							onConfirm={save}
						>
							<Button type="primary" icon={<SaveOutlined />} loading={busy} disabled={loading}>
								Save
							</Button>
						</Popconfirm>
					</Space>
					<Paragraph type="secondary" style={{ marginTop: 12, fontSize: 12 }}>
						Test with latest file runs a dry run with this UNSAVED doc against the
						company's most recent file — when it finishes (~1 min), open the row's
						History and use the Report button to review the would-be writes before
						saving. Saving affects the next live run immediately; no deploy.
					</Paragraph>
				</div>
			) : null}
		</Drawer>
	);
};

export default MappingEditorDrawer;
